import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AttachFilesSection } from '@/components/shared'
import type { AttachmentRef } from '@/components/shared'
import { invoicesApi } from '@/services/invoices'
import { clientsApi } from '@/services/clients'
import { settingsApi } from '@/services/settings'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { DOCUMENT_FILE_ACCEPT } from '@/lib/constants'

const INVOICE_DOCUMENTS_STALE_TIME = 5 * 60_000

const invoiceDocumentsQueryKey = (invoiceId: string) =>
  ['invoice-documents', invoiceId] as const

interface Props {
  invoiceId: string
  clientId?: string
  hideHeading?: boolean
  brandTabs?: boolean
  readOnly?: boolean
  onCountChange?: (count: number) => void
}

function attachmentSource(
  docType: string | null | undefined
): AttachmentRef['source'] {
  if (docType === 'client' || docType === 'library') return docType
  return 'upload'
}

// Invoice attachments (feedback #44). Wraps the shared AttachFilesSection,
// wiring it to the invoice document endpoints (upload new / pick from the
// client's documents / pick from the Settings document library). Mirrors
// QuoteAttachments. Persists each change immediately.
export function InvoiceAttachments({
  invoiceId,
  clientId,
  hideHeading,
  brandTabs,
  readOnly,
  onCountChange,
}: Props) {
  const { t } = useTranslation('quotes')
  const queryClient = useQueryClient()
  const { data: value = [] } = useQuery({
    queryKey: invoiceDocumentsQueryKey(invoiceId),
    queryFn: async () => {
      const res = await invoicesApi.listDocuments(invoiceId, { limit: 100 })
      return (res.data ?? []).map(d => ({
        id: d.id,
        file_id: d.file_id,
        name: d.file_name ?? t('attachments.untitled'),
        url: d.url,
        mime_type: d.file_type,
        size: d.file_size,
        created_at: d.created_at,
        source: attachmentSource(d.doc_type),
      }))
    },
    staleTime: INVOICE_DOCUMENTS_STALE_TIME,
    retry: false,
  })

  const setValue = useCallback(
    (
      next: AttachmentRef[] | ((current: AttachmentRef[]) => AttachmentRef[])
    ) => {
      queryClient.setQueryData<AttachmentRef[]>(
        invoiceDocumentsQueryKey(invoiceId),
        current => {
          const previous = current ?? []
          return typeof next === 'function' ? next(previous) : next
        }
      )
    },
    [invoiceId, queryClient]
  )

  useEffect(() => {
    onCountChange?.(value.length)
  }, [onCountChange, value.length])

  const uploadFn = useCallback(
    async (file: File): Promise<AttachmentRef> => {
      const res = await invoicesApi.uploadDocument(invoiceId, file)
      const d = res.data!
      return {
        id: d.id,
        file_id: d.file_id,
        name: d.file_name ?? file.name,
        url: d.url,
        mime_type: d.file_type,
        size: d.file_size,
        created_at: d.created_at,
        source: 'upload',
      }
    },
    [invoiceId]
  )

  const listClientDocsFn = useCallback(
    async (cId: string): Promise<AttachmentRef[]> => {
      const res = await clientsApi.documents(cId, { limit: 100 })
      return (res.data ?? []).map(d => ({
        id: d.file_id,
        file_id: d.file_id,
        name: d.file_name,
        url: d.url,
        mime_type: d.file_type,
        size: d.file_size,
        created_at: d.created_at,
        source: 'client' as const,
      }))
    },
    []
  )

  const listLibraryFn = useCallback(async (): Promise<AttachmentRef[]> => {
    const res = await settingsApi.reusableDocumentOptions('invoice')
    return (res.data ?? []).map(d => ({
      id: d.file_id,
      file_id: d.file_id,
      name: d.name,
      created_at: d.created_at,
      source: 'library' as const,
    }))
  }, [])

  const onChange = useCallback(
    async (next: AttachmentRef[]) => {
      const prev = value
      setValue(next)

      const added = next.filter(n => !prev.some(p => sameRef(p, n)))
      const removed = prev.filter(p => !next.some(n => sameRef(p, n)))

      try {
        for (const ref of added) {
          if (ref.source === 'upload') continue
          if (!ref.id) continue
          const res = await invoicesApi.attachExistingDocument(
            invoiceId,
            ref.id,
            ref.source
          )
          setValue(curr =>
            curr.map(c => (sameRef(c, ref) ? { ...c, id: res.data?.id } : c))
          )
        }
        for (const ref of removed) {
          if (ref.id) await invoicesApi.removeDocument(invoiceId, ref.id)
        }
      } catch (e) {
        setValue(prev)
        toast({
          variant: 'destructive',
          title: getApiErrorMessage(e, t('attachments.error')),
        })
      }
    },
    [invoiceId, setValue, value, t]
  )

  const onRename = useCallback(
    async (item: AttachmentRef, name: string) => {
      if (item.source !== 'upload' || !item.id) return
      const response = await invoicesApi.renameDocument(
        invoiceId,
        item.id,
        name
      )
      toast({ variant: 'success', title: t('attachments.renameSuccess') })
      return {
        name: response.data?.file_name ?? item.name,
        url: response.data?.url ?? item.url,
      }
    },
    [invoiceId, t]
  )

  if (readOnly) {
    return (
      <AttachFilesSection
        value={value}
        onChange={() => undefined}
        hideHeading={hideHeading}
        readOnly
        emptyMessage={t('attachments.none')}
      />
    )
  }

  return (
    <AttachFilesSection
      value={value}
      onChange={onChange}
      clientId={clientId}
      accept={DOCUMENT_FILE_ACCEPT}
      uploadFn={uploadFn}
      listClientDocsFn={clientId ? listClientDocsFn : undefined}
      listLibraryFn={listLibraryFn}
      libraryTarget='invoice'
      preloadLibrary
      onRename={onRename}
      hideHeading={hideHeading}
      brandTabs={brandTabs}
    />
  )
}

function sameRef(a: AttachmentRef, b: AttachmentRef): boolean {
  if (a.file_id && b.file_id) return a.file_id === b.file_id
  if (a.id && b.id) return a.id === b.id
  return a.name === b.name && (a.url ?? '') === (b.url ?? '')
}

export default InvoiceAttachments
